import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createLead, uploadAttachments, fetchCategories, fetchLeads } from '../../store/slices/leadsSlice';
import { fetchClients } from '../../store/slices/clientsSlice';
import { fetchCities } from '../../store/slices/citiesSlice';
import ProjectManagerSearch from './ProjectManagerSearch';
import { showToast } from '../utils/Toast';
import { US_STATES, US_CITIES } from '../../data/locationData';
import { PRIORITIZED_CITIES } from '../../data/cityData';
import MultiSelectDropdown from './MultiSelectDropdown';
import SearchableDropdown from '../common/SearchableDropdown';
import EmailTagInput from '../common/EmailTagInput';


// Client search select component
const ClientSearchSelect = ({ clients, value, onChange, className }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filteredClients, setFilteredClients] = useState(clients || []);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const dropdownRef = useRef(null);

  // Get the selected client display text
  const selectedClient = clients.find(client => client._id === value);
  const displayText = selectedClient
    ? `${selectedClient.name} ${selectedClient.email ? `(${selectedClient.email})` : ''}`
    : '-- Select Client --';

  // Sort clients alphabetically
  const sortClients = (clientsToSort, order) => {
    return [...clientsToSort].sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();

      if (order === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
  };

  // Filter and sort clients when search term or sort order changes
  useEffect(() => {
    let filtered;

    if (!searchTerm.trim()) {
      filtered = clients || [];
    } else {
      const searchTermLower = searchTerm.toLowerCase();
      filtered = (clients || []).filter(client =>
        client.name?.toLowerCase().includes(searchTermLower) ||
        client.email?.toLowerCase().includes(searchTermLower) ||
        client.cellPhone?.includes(searchTerm)
      );
    }

    // Apply sorting
    const sorted = sortClients(filtered, sortOrder);
    setFilteredClients(sorted);
  }, [searchTerm, clients, sortOrder]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle selection and close dropdown
  const handleSelect = (clientId) => {
    onChange(clientId);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Clear selection
  const handleClear = () => {
    onChange('');
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        className={`${className} cursor-pointer flex justify-between items-center`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="truncate">{displayText}</div>
        <svg className="w-5 h-5 ml-2" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
        </svg>
      </div>

      {isOpen && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          <div className="p-2 sticky top-0 bg-white border-b">
            <input
              type="text"
              className="w-full p-2 border border-gray-300 rounded-md mb-2"
              placeholder="Search clients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">Sort:</span>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortOrder('asc');
                }}
                className={`px-2 py-1 text-xs rounded ${sortOrder === 'asc'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                A-Z
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setSortOrder('desc');
                }}
                className={`px-2 py-1 text-xs rounded ${sortOrder === 'desc'
                  ? 'bg-blue-100 text-blue-700 border border-blue-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
              >
                Z-A
              </button>
            </div>
          </div>

          <div className="py-1">
            <div
              className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                handleClear();
              }}
            >
              <span className="font-medium text-gray-500">Clear Selection</span>
            </div>

            {filteredClients.length === 0 ? (
              <div className="px-4 py-2 text-sm text-gray-500">No clients found</div>
            ) : (
              filteredClients.map(client => (
                <div
                  key={client._id}
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelect(client._id)}
                >
                  {client.name} {client.email && <span className="text-gray-500">({client.email})</span>}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const NewLead = ({ isOpen, onClose }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedManagers, setSelectedManagers] = useState([]);
  const [isTabbedView, setIsTabbedView] = useState(false);
  const [isMaximized, setIsMaximized] = useState(true);
  const [formHistory, setFormHistory] = useState([]);
  const [currentHistoryIndex, setCurrentHistoryIndex] = useState(-1);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [sameBillingAsClient, setSameBillingAsClient] = useState(false);
  const [sameSiteAsClient, setSameSiteAsClient] = useState(false);
  const [formData, setFormData] = useState({
    // Step 1 - Lead Information
    leadContactName: '',
    leadContactPhone: '',
    leadContactEmail: '',
    leadAddress: '',
    leadCity: '',
    leadNotes: '',
    categoryId: '',

    // Step 2 - Client Information
    clientName: '',
    clientAddress: '',
    clientCity: '',
    clientState: '',
    clientZipCode: '',
    clientCellPhone: '',
    clientAltPhone: '',
    clientEmail: '',
    clientEmails: [],

    // New Billing Information section
    billingContactName: '',
    billingPhone: '',
    billingEmail: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZipCode: '',

    // Step 3 - Site Contact
    siteContactName: '',
    siteContactAddress: '',
    siteContactCity: '',
    siteContactState: '',
    siteContactZipCode: '',
    siteContactCellPhone: '',
    siteContactAltPhone: '',
    siteContactEmail: '',

    // Step 4
    attachments: [],

    stepData: {
      step1Complete: false,
      step2Complete: false,
      step3Complete: false,
      step4Complete: false
    }
  });

  const [editableFields, setEditableFields] = useState({
    city: false,
    state: false,
    clientCity: false,
    clientState: false,
    siteContactCity: false,
    siteContactState: false
  });

  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.leads);
  const { clients = [] } = useSelector(state => state.clients || {});
  const { categories = [] } = useSelector(state => state.leads || {});
  const cities = useSelector(state => state.cities?.items || []);
  const citiesLoading = useSelector(state => state.cities?.loading);
  const citiesError = useSelector(state => state.cities?.error);

  // Transform cities for dropdown
  const cityOptions = React.useMemo(() => {
    console.log('Transforming cities to options:', cities);
    return cities.map(city => ({
      label: city.name,
      value: city.code,
      name: city.name,
      state: city.state,
      code: city.code
    }));
  }, [cities]);

  // Add city loading effect
  useEffect(() => {
    if (isOpen) {
      console.log('Fetching cities...');
      dispatch(fetchCities())
        .unwrap()
        .then(result => {
          console.log('Cities fetched successfully:', result);
        })
        .catch(error => {
          console.error('Error fetching cities:', error);
        });
    }
  }, [isOpen, dispatch]);

  // Add debugging useEffect for cities
  useEffect(() => {
    console.log('Current cities state:', cities);
    console.log('Cities loading:', citiesLoading);
    console.log('Cities error:', citiesError);
  }, [cities, citiesLoading, citiesError]);

  // Update all SearchableDropdown components for cities to use cityOptions
  // Replace all instances of options={PRIORITIZED_CITIES} with options={cityOptions}
  // And update the value and onChange handlers to handle the new city object format

  // Update city selection handlers
  const handleCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      leadCity: selectedCity?.name || ''
    }));
  };

  const handleClientCityChange = (selectedCity) => {
    handleClientFieldChange('clientCity', selectedCity?.name || '');
  };

  const handleBillingCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      billingCity: selectedCity?.name || ''
    }));
  };

  const handleSiteContactCityChange = (selectedCity) => {
    setFormData(prev => ({
      ...prev,
      siteContactCity: selectedCity?.name || ''
    }));
  };

  // Find and replace all SearchableDropdown components for cities
  // Replace:
  // options={PRIORITIZED_CITIES}
  // With:
  // options={cityOptions}
  // value={cityOptions.find(opt => opt.name === formData.leadCity) || ''}
  // onChange={handleCityChange}
  // getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}

  // Add activeTab state
  const [activeTab, setActiveTab] = useState('Lead Info');

  // Add this state at the top of the component with other states
  const [previewImage, setPreviewImage] = useState(null);

  // Add these new state variables and refs
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const dropAreaRef = useRef(null);

  // Add a state to store file preview URLs
  const [filePreviewUrls, setFilePreviewUrls] = useState({});

  // Fetch clients when the component mounts
  useEffect(() => {
    if (isOpen) {
      dispatch(fetchClients({ limit: 1000 })); // Fetch all clients
      dispatch(fetchCategories());
    }
  }, [isOpen, dispatch]);

  // Add effect to copy client info to billing when checkbox is toggled
  useEffect(() => {
    if (sameBillingAsClient) {
      setFormData(prev => ({
        ...prev,
        billingContactName: prev.clientName,
        billingPhone: prev.clientCellPhone,
        billingEmail: prev.clientEmail,
        billingAddress: prev.clientAddress,
        billingCity: prev.clientCity,
        billingState: prev.clientState,
        billingZipCode: prev.clientZipCode
      }));
    }
  }, [sameBillingAsClient, formData.clientName, formData.clientCellPhone, formData.clientEmail,
    formData.clientAddress, formData.clientCity, formData.clientState, formData.clientZipCode]);

  // Add effect to copy client info to site contact when checkbox is toggled
  // Deliberately NOT copying address fields - the Job Address must remain
  // the address the user typed in the Lead Info tab, independent of the client.
  useEffect(() => {
    if (sameSiteAsClient) {
      setFormData(prev => ({
        ...prev,
        siteContactName: prev.clientName,
        siteContactCellPhone: prev.clientCellPhone,
        siteContactAltPhone: prev.clientAltPhone,
        siteContactEmail: prev.clientEmail
      }));
    }
  }, [sameSiteAsClient, formData.clientName, formData.clientCellPhone, formData.clientAltPhone, formData.clientEmail]);

  // Add handler for client field changes to update dependent fields
  const handleClientFieldChange = (field, value) => {
    const updates = { [field]: value };

    // Special handling for clientEmails array
    if (field === 'clientEmails') {
      // Convert array to comma-separated string for clientEmail field
      updates.clientEmail = value.join(', ');
    }

    // Update the client field
    setFormData(prev => ({
      ...prev,
      ...updates
    }));

    // If same billing info is checked, update billing fields
    if (sameBillingAsClient) {
      const billingUpdates = {};
      if (field === 'clientName') billingUpdates.billingContactName = value;
      if (field === 'clientCellPhone') billingUpdates.billingPhone = value;
      if (field === 'clientEmail') billingUpdates.billingEmail = value;
      if (field === 'clientEmails') billingUpdates.billingEmail = value.join(', ');
      if (field === 'clientAddress') billingUpdates.billingAddress = value;
      if (field === 'clientCity') billingUpdates.billingCity = value;
      if (field === 'clientState') billingUpdates.billingState = value;
      if (field === 'clientZipCode') billingUpdates.billingZipCode = value;

      if (Object.keys(billingUpdates).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...billingUpdates
        }));
      }
    }

    // If same site contact info is checked, update site contact fields
    // Deliberately NOT propagating address fields so the Job Address is preserved.
    if (sameSiteAsClient) {
      const siteUpdates = {};
      if (field === 'clientName') siteUpdates.siteContactName = value;
      if (field === 'clientCellPhone') siteUpdates.siteContactCellPhone = value;
      if (field === 'clientAltPhone') siteUpdates.siteContactAltPhone = value;
      if (field === 'clientEmail') siteUpdates.siteContactEmail = value;
      if (field === 'clientEmails') siteUpdates.siteContactEmail = value.join(', ');

      if (Object.keys(siteUpdates).length > 0) {
        setFormData(prev => ({
          ...prev,
          ...siteUpdates
        }));
      }
    }
  };

  // Handle email tag input changes
  const handleEmailTagsChange = (emails) => {
    handleClientFieldChange('clientEmails', emails);
  };

  // Function to handle client selection
  const handleClientSelect = (clientId) => {
    setSelectedClientId(clientId);

    if (!clientId) {
      // Clear client fields if no selection
      setFormData(prev => ({
        ...prev,
        clientName: '',
        clientEmail: '',
        clientCellPhone: '',
        clientAltPhone: '',
        clientAddress: '',
        clientCity: '',
        clientState: '',
        clientZipCode: ''
      }));
      return;
    }

    const selectedClient = clients.find(client => client._id === clientId);
    if (selectedClient) {
      // Convert email string to array if it contains commas
      const emailString = selectedClient.email || '';
      const emailArray = emailString.split(',').map(email => email.trim()).filter(Boolean);

      const clientUpdates = {
        clientName: selectedClient.name || '',
        clientEmail: selectedClient.email || '',
        clientEmails: emailArray,
        clientCellPhone: selectedClient.cellPhone || '',
        clientAltPhone: selectedClient.altPhone || '',
        clientAddress: selectedClient.address?.street || '',
        clientCity: selectedClient.address?.city || '',
        clientState: selectedClient.address?.state || '',
        clientZipCode: selectedClient.address?.zipCode || ''
      };

      setFormData(prev => ({
        ...prev,
        ...clientUpdates
      }));

      // If same billing info is checked, update billing fields
      if (sameBillingAsClient) {
        setFormData(prev => ({
          ...prev,
          billingContactName: clientUpdates.clientName,
          billingPhone: clientUpdates.clientCellPhone,
          billingEmail: clientUpdates.clientEmail,
          billingAddress: clientUpdates.clientAddress,
          billingCity: clientUpdates.clientCity,
          billingState: clientUpdates.clientState,
          billingZipCode: clientUpdates.clientZipCode
        }));
      }

      // If same site contact info is checked, update site contact fields
      // Deliberately NOT updating address fields so the Job Address is not
      // overwritten when an existing client is selected from the dropdown.
      if (sameSiteAsClient) {
        setFormData(prev => ({
          ...prev,
          siteContactName: clientUpdates.clientName,
          siteContactCellPhone: clientUpdates.clientCellPhone,
          siteContactAltPhone: clientUpdates.clientAltPhone,
          siteContactEmail: clientUpdates.clientEmail
        }));
      }
    }
  };

  // Clean up object URLs when component unmounts or when files change
  useEffect(() => {
    return () => {
      // Revoke all object URLs to avoid memory leaks
      Object.values(filePreviewUrls).forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [filePreviewUrls]);

  // Reset form and preview URLs when dialog opens/closes
  useEffect(() => {
    if (isOpen) {
      setFormData({
        leadContactName: '',
        leadContactPhone: '',
        leadContactEmail: '',
        leadAddress: '',
        leadCity: '',
        leadNotes: '',
        clientName: '',
        clientAddress: '',
        clientCity: '',
        clientState: '',
        clientZipCode: '',
        clientCellPhone: '',
        clientAltPhone: '',
        clientEmail: '',
        clientEmails: [],
        siteContactName: '',
        siteContactAddress: '',
        siteContactCity: '',
        siteContactState: '',
        siteContactZipCode: '',
        siteContactCellPhone: '',
        siteContactAltPhone: '',
        siteContactEmail: '',
        attachments: [],
        stepData: {
          step1Complete: false,
          step2Complete: false,
          step3Complete: false,
          step4Complete: false
        }
      });
      setCurrentStep(1);
      setSelectedManagers([]);
      setSelectedClientId('');
      setFilePreviewUrls({});
    }
  }, [isOpen]);

  // Track form changes for undo functionality
  useEffect(() => {
    if (isOpen && Object.keys(formData).length > 0) {
      // Only add to history if it's a meaningful change
      if (currentHistoryIndex === -1 || JSON.stringify(formData) !== JSON.stringify(formHistory[currentHistoryIndex])) {
        // Trim the history array if we're not at the end
        const newHistory = currentHistoryIndex < formHistory.length - 1
          ? formHistory.slice(0, currentHistoryIndex + 1)
          : [...formHistory];

        setFormHistory([...newHistory, { ...formData }]);
        setCurrentHistoryIndex(newHistory.length);
      }
    }
  }, [formData]);

  // Function to undo changes
  const handleUndo = () => {
    if (currentHistoryIndex > 0) {
      const prevIndex = currentHistoryIndex - 1;
      setFormData({ ...formHistory[prevIndex] });
      setCurrentHistoryIndex(prevIndex);
    }
  };

  // Reset history when dialog opens
  useEffect(() => {
    if (isOpen) {
      setFormHistory([{ ...formData }]);
      setCurrentHistoryIndex(0);
    }
  }, [isOpen]);

  const handleManagerSelect = (manager) => {
    setSelectedManagers(prev => [...prev, manager]);
  };

  const handleManagerRemove = (managerId) => {
    setSelectedManagers(prev => prev.filter(manager => manager._id !== managerId));
  };

  const validateForm = () => {
    // Check the required fields for the leadContact
    if (!formData.leadContactName || formData.leadContactName.trim() === '') {
      showToast('Contact name is required', 'error');
      setActiveTab('Lead Info');
      return false;
    }

    if (!formData.clientEmail || formData.clientEmail.trim() === '') {
      showToast('Client Email is required', 'error');
      setActiveTab('Client Info');
      return false;
    }

    // Email validation is now handled by the EmailTagInput component
    // Just check if there are any emails
    if (formData.clientEmails.length === 0) {
      showToast('At least one email address is required', 'error');
      setActiveTab('Client Info');
      return false;
    }

    if (formData.leadCity === '') {
      showToast('City is required', 'error');
      setActiveTab('Lead Info');
      return false;
    }

    if (selectedManagers.length === 0) {
      showToast('Please select at least one project manager', 'error');
      setActiveTab('Lead Info');
      return false;
    }

    return true;
  };

  const handleCreateLead = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // Prepare lead data with selected client ID if available
      const leadData = {
        projectManagers: selectedManagers.map(manager => manager._id),
        leadContact: {
          name: formData.leadContactName,
          email: formData.clientEmails.length > 0 ? formData.clientEmails.join(', ') : formData.clientEmail,
          phone: formData.leadContactPhone,
          notes: formData.leadNotes
        },
        // Add city in the address section
        address: {
          city: formData.leadCity,
          // Include other address fields if available
          street: formData.leadAddress,
          state: '',
          zipCode: ''
        },
        // If we have a selected client, just send the ID
        clientInfo: selectedClientId ?
          { _id: selectedClientId } :
          {
            name: formData.clientName,
            email: formData.clientEmails.length > 0 ? formData.clientEmails.join(', ') : formData.clientEmail,
            cellPhone: formData.clientCellPhone,
            altPhone: formData.clientAltPhone,
            address: formData.clientAddress,
            city: formData.clientCity,
            state: formData.clientState,
            zipCode: formData.clientZipCode
          },
        siteContact: {
          name: formData.siteContactName,
          email: formData.siteContactEmail,
          cellPhone: formData.siteContactCellPhone,
          altPhone: formData.siteContactAltPhone,
          // Persist the user's typed Job Address as siteContact.address.street
          // so it shows correctly as "Job Address" when viewing the lead later.
          address: formData.leadAddress,
          city: formData.siteContactCity,
          state: formData.siteContactState,
          zipCode: formData.siteContactZipCode
        },
        // Add billing information
        billingContact: {
          name: formData.billingContactName,
          email: formData.billingEmail,
          phone: formData.billingPhone,
          address: {
            street: formData.billingAddress,
            city: formData.billingCity,
            state: formData.billingState,
            zipCode: formData.billingZipCode
          }
        },
        // Add category ID if selected
        ...(formData.categoryId && { categoryId: formData.categoryId })
      };

      // Display loading state if there are files to upload
      if (formData.attachments.length > 0) {
        showToast('Creating lead and uploading files...', 'info');
      }

      const leadResponse = await dispatch(createLead(leadData)).unwrap();

      if (leadResponse.lead && leadResponse.lead.jobNumber) {
        showToast(`Lead created successfully. Job Number: ${leadResponse.lead.jobNumber}`, 'success');
      } else {
        showToast('Lead created successfully', 'success');
      }

      // Refresh leads list to show the new lead at the top
      dispatch(fetchLeads({
        page: 1,
        limit: 10,
        sortField: 'createdAt',
        sortOrder: 'desc'
      }));

      // Handle file uploads if there are any
      if (formData.attachments.length > 0) {
        try {
          await dispatch(uploadAttachments({
            leadId: leadResponse.lead._id,
            files: formData.attachments
          })).unwrap();
          showToast('All files uploaded successfully', 'success');
        } catch (uploadError) {
          console.error('Error uploading files:', uploadError);
          showToast(`Lead created, but some files failed to upload: ${uploadError.message || 'Unknown error'}`, 'warning');
        }
      }

      onClose();
    } catch (error) {
      console.error('Error creating lead:', error);
      showToast(error.message || 'Error creating lead', 'error');
    }
  };

  const handleBack = () => {
    const updatedFormData = {
      ...formData,
    };
    setFormData(updatedFormData);
    setCurrentStep(currentStep - 1);
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);

    // Validate file size (max 5GB)
    const oversizedFiles = newFiles.filter(file => file.size > 5 * 1024 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      showToast('Some files exceed the 5GB size limit and will not be uploaded', 'error');
    }

    // Filter out files that are too large
    const validFiles = newFiles.filter(file => file.size <= 5 * 1024 * 1024 * 1024);

    // Generate preview URLs for the new files
    const newPreviewUrls = {};
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        newPreviewUrls[file.name] = URL.createObjectURL(file);
      }
    });

    // Add new files to existing files
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));

    // Update preview URLs
    setFilePreviewUrls(prev => ({
      ...prev,
      ...newPreviewUrls
    }));
  };

  // Toggle between tabbed and non-tabbed view
  const toggleView = () => {
    setIsTabbedView(!isTabbedView);
  };

  // Toggle maximize/minimize
  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  // Replace renderStepIndicator with tabbed navigation
  const renderTabNavigation = () => {
    const tabs = ['Lead Info', 'Client Info', 'Project/Contact Info', 'Billing Info', 'Notes & Files'];

    return (
      <div className="mb-4">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-4" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`
                  whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm
                  ${activeTab === tab
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }
                `}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>
      </div>
    );
  };

  // Add this helper function near the top of the component
  const handleNext = () => {
    const tabs = ['Lead Info', 'Client Info', 'Project/Contact Info', 'Billing Info', 'Notes & Files'];
    const currentIndex = tabs.indexOf(activeTab);
    if (currentIndex < tabs.length - 1) {
      setActiveTab(tabs[currentIndex + 1]);
    }
  };

  // Add the renderFileItem function
  const renderFileItem = (file, index) => {
    const isImage = file.type.startsWith('image/');
    const previewUrl = isImage ? filePreviewUrls[file.name] || URL.createObjectURL(file) : null;

    // If we're creating a new preview URL, store it
    if (isImage && !filePreviewUrls[file.name]) {
      setFilePreviewUrls(prev => ({
        ...prev,
        [file.name]: previewUrl
      }));
    }

    return (
      <div
        key={index}
        className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
      >
        <div className="flex items-center gap-2">
          {isImage ? (
            <img
              src={previewUrl}
              alt={file.name}
              className="w-10 h-10 object-cover rounded cursor-pointer"
              onClick={() => setPreviewImage(previewUrl)}
            />
          ) : (
            <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          )}
          <span className="text-sm text-gray-600 truncate">
            {file.name}
          </span>
        </div>
        <button
          type="button"
          onClick={() => {
            // Remove the file from attachments
            setFormData(prev => ({
              ...prev,
              attachments: prev.attachments.filter((_, i) => i !== index)
            }));

            // Clean up the preview URL if it exists
            if (filePreviewUrls[file.name]) {
              URL.revokeObjectURL(filePreviewUrls[file.name]);
              setFilePreviewUrls(prev => {
                const updated = { ...prev };
                delete updated[file.name];
                return updated;
              });
            }
          }}
          className="text-red-500 hover:text-red-700"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    );
  };

  // Update the renderTabContent function to use renderFileItem
  const renderTabContent = () => {
    const renderNextButton = () => (
      <div className="flex justify-end mt-6 gap-2">
        <button
          type="button"
          onClick={handleNext}
          className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded-lg focus:outline-none"
        >
          Next
        </button>
        <button
          type="button"
          onClick={handleCreateLead}
          className="px-4 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg focus:outline-none"
        >
          Create Lead
        </button>
      </div>
    );

    const labelClasses = "block text-xs uppercase text-gray-500 mb-1 flex items-center gap-1";

    const RequiredLabel = ({ children, showInfo, infoText }) => (
      <div className={labelClasses}>
        <div className="flex items-center gap-2">
          <span>{children}</span>
          <span className="text-red-500">*</span>
          {showInfo && (
            <div className="group relative">
              <svg
                className="w-4 h-4 text-gray-400 cursor-help"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                {infoText}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    switch (activeTab) {
      case 'Lead Info':
        return (
          <div className="space-y-4">
            <h2 className="text-center font-normal text-lg mb-4">NEW LEAD</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="md:col-span-2">
                <RequiredLabel>Job Name</RequiredLabel>
                <input
                  type="text"
                  className={`${inputClasses} ${!formData.leadContactName ? 'border-red-300' : ''}`}
                  placeholder="Enter job name"
                  value={formData.leadContactName}
                  onChange={(e) => setFormData({ ...formData, leadContactName: e.target.value })}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <RequiredLabel>Job Address</RequiredLabel>
                <textarea
                  className={`${textAreaClasses} ${!formData.leadAddress ? 'border-red-300' : ''}`}
                  placeholder="Enter job address"
                  value={formData.leadAddress}
                  onChange={(e) => setFormData({ ...formData, leadAddress: e.target.value })}
                  required
                />
              </div>

              <div className="md:col-span-2">
                <RequiredLabel>City</RequiredLabel>
                <SearchableDropdown
                  key={`lead-city-${activeTab}`}
                  options={cityOptions}
                  value={cityOptions.find(opt => opt.name === formData.leadCity) || ''}
                  onChange={handleCityChange}
                  getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                  placeholder="Search city..."
                  className={`${inputClasses} ${!formData.leadCity ? 'border-red-300' : ''}`}
                />
              </div>
            </div>
            {renderNextButton()}
          </div>
        );

      case 'Client Info':
        return (
          <div className="space-y-4">
            <h2 className="text-center font-normal text-lg mb-4">CLIENT INFORMATION</h2>

            {/* Add Client Selector */}
            <div className="mb-4">
              <label className={labelClasses}>Choose from existing client</label>
              <ClientSearchSelect
                clients={clients}
                value={selectedClientId}
                onChange={handleClientSelect}
                className={inputClasses}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="md:col-span-2">
                <RequiredLabel>Client Name</RequiredLabel>
                <input
                  type="text"
                  className={`${inputClasses} ${!formData.clientName ? 'border-red-300' : ''}`}
                  placeholder="Enter client name"
                  value={formData.clientName}
                  onChange={(e) => handleClientFieldChange('clientName', e.target.value)}
                  required
                />
              </div>
              <div className="md:col-span-2">
                <label className={labelClasses}>Client Address</label>
                <textarea
                  className={textAreaClasses}
                  placeholder="Enter client address"
                  value={formData.clientAddress}
                  onChange={(e) => handleClientFieldChange('clientAddress', e.target.value)}
                />
              </div>
              <div>
                <label className={labelClasses}>Client Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.clientCellPhone}
                  onChange={(e) => handleClientFieldChange('clientCellPhone', e.target.value)}
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between">
                  <RequiredLabel>Client Email</RequiredLabel>
                  <span className="text-xs text-gray-500">Type email and press Enter or comma to add</span>
                </div>
                <EmailTagInput
                  emails={formData.clientEmails}
                  onChange={handleEmailTagsChange}
                  placeholder="Enter email address(es)"
                  required
                  className={!formData.clientEmails.length ? 'border-red-300' : ''}
                />
              </div>
            </div>
            {renderNextButton()}
          </div>
        );

      case 'Billing Info':
        return (
          <div className="space-y-4">
            <h2 className="text-center font-normal text-lg mb-4">BILLING INFORMATION</h2>

            <div className="mb-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="same-billing-as-client-tab"
                  checked={sameBillingAsClient}
                  onChange={() => setSameBillingAsClient(!sameBillingAsClient)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="same-billing-as-client-tab" className="ml-2 text-sm text-gray-700">
                  Same as client information
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              <div className="md:col-span-2">
                <label className={labelClasses}>Billing Name</label>
                <input
                  type="text"
                  className={inputClasses}
                  placeholder="Enter billing name"
                  value={formData.billingContactName}
                  onChange={(e) => setFormData({ ...formData, billingContactName: e.target.value })}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div className="md:col-span-2">
                <label className={labelClasses}>Billing Address</label>
                <textarea
                  className={textAreaClasses}
                  placeholder="Enter billing address"
                  value={formData.billingAddress}
                  onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Billing Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.billingPhone}
                  onChange={(e) => setFormData({ ...formData, billingPhone: e.target.value })}
                  disabled={sameBillingAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Billing Email Address</label>
                <input
                  type="email"
                  className={inputClasses}
                  placeholder="Enter billing email address"
                  value={formData.billingEmail}
                  onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                  disabled={sameBillingAsClient}
                />
              </div>
            </div>
            {renderNextButton()}
          </div>
        );

      case 'Project/Contact Info':
        return (
          <div className="space-y-4">
            <h2 className="text-center font-normal text-lg mb-4">PROJECT/CONTACT INFO</h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">



              <div className="mb-4 md:col-span-2">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="same-site-as-client-tab"
                    checked={sameSiteAsClient}
                    onChange={() => setSameSiteAsClient(!sameSiteAsClient)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="same-site-as-client-tab" className="ml-2 text-sm text-gray-700">
                    Same as client information
                  </label>
                </div>
              </div>

              <div className="md:col-span-2">
                <label className={labelClasses}>Contact Name</label>
                <input
                  type="text"
                  className={inputClasses}
                  placeholder="Enter contact name"
                  value={formData.siteContactName}
                  onChange={(e) => setFormData({ ...formData, siteContactName: e.target.value })}
                  disabled={sameSiteAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Contact Phone</label>
                <input
                  type="tel"
                  className={inputClasses}
                  placeholder="(000) 000-0000"
                  value={formData.siteContactCellPhone}
                  onChange={(e) => setFormData({ ...formData, siteContactCellPhone: e.target.value })}
                  disabled={sameSiteAsClient}
                />
              </div>

              <div>
                <label className={labelClasses}>Contact Email</label>
                <input
                  type="email"
                  className={inputClasses}
                  placeholder="Enter email address"
                  value={formData.siteContactEmail}
                  onChange={(e) => setFormData({ ...formData, siteContactEmail: e.target.value })}
                  disabled={sameSiteAsClient}
                />
              </div>
            </div>
            {renderNextButton()}
          </div>
        );

      case 'Notes & Files':
        return (
          <div className="space-y-4">
            <div className="mb-6">
              <h2 className="text-center font-normal text-lg mb-4">PROJECT MANAGER(S)</h2>
              <div className="md:col-span-2">
                <div className="flex items-center gap-2">
                  <RequiredLabel
                    showInfo={true}
                    infoText="At least one project manager must be selected"
                  >
                    Project Manager(s)
                  </RequiredLabel>
                </div>
                <MultiSelectDropdown
                  selectedManagers={selectedManagers}
                  onManagerSelect={handleManagerSelect}
                  onManagerRemove={handleManagerRemove}
                />
                {selectedManagers.length === 0 &&
                  <p className="text-red-500 text-xs mt-1">Project manager is required</p>
                }
              </div>
            </div>

            <h2 className="text-center font-normal text-lg mb-4">NOTES</h2>
            <div className="mb-6">
              <textarea
                className={`${textAreaClasses} h-32`}
                placeholder="Enter any additional notes..."
                value={formData.leadNotes}
                onChange={(e) => setFormData({ ...formData, leadNotes: e.target.value })}
              />
            </div>

            <h2 className="text-center font-normal text-lg mb-4">Attachments</h2>

            {/* Show selected files if any */}
            {formData.attachments.length > 0 && (
              <div className="mb-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {formData.attachments.map((file, index) => renderFileItem(file, index))}
                </div>
              </div>
            )}

            {/* Upload area with drag and drop */}
            <div
              ref={dropAreaRef}
              className={`border-2 border-dashed ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded-lg p-8`}
              onDragEnter={handleDragEnter}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <div className="flex flex-col items-center justify-center">
                <svg className={`w-16 h-16 ${isDragging ? 'text-blue-500' : 'text-gray-400'} mb-4`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <span className="text-sm text-gray-500">{isDragging ? 'Drop files here' : 'Select a file or drag and drop here'}</span>
                <span className="text-xs text-gray-400 mt-1">(JPG, PNG or PDF file size no more than 5GB)</span>
                <label htmlFor="file-upload-tab" className="mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 cursor-pointer">
                  SELECT FILE
                </label>
                <input
                  ref={fileInputRef}
                  id="file-upload-tab"
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  className="hidden"
                />
              </div>
            </div>
            {renderNextButton()}
          </div>
        );

      default:
        return null;
    }
  };

  const inputClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white
  `;

  const textAreaClasses = `
    w-full border border-gray-200 px-4 py-2.5 
    focus:outline-none focus:border-blue-500 text-gray-900
    bg-white resize-none h-24
  `;

  // Add this component inside NewLead but before the return statement
  const ImagePreviewDialog = ({ isOpen, onClose, imageUrl }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75">
        <div className="relative max-w-4xl max-h-[90vh] overflow-auto">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white hover:text-gray-300"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <img
            src={imageUrl}
            alt="Preview"
            className="max-w-full max-h-[90vh] object-contain"
          />
        </div>
      </div>
    );
  };

  const renderNonTabbedContent = () => {
    const labelClasses = "block text-xs uppercase text-gray-500 mb-1 flex items-center gap-1";

    const RequiredLabel = ({ children, showInfo, infoText }) => (
      <div className={labelClasses}>
        <div className="flex items-center gap-2">
          <span>{children}</span>
          <span className="text-red-500">*</span>
          {showInfo && (
            <div className="group relative">
              <svg
                className="w-4 h-4 text-gray-400 cursor-help"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-800 text-white text-xs rounded shadow-lg">
                {infoText}
              </div>
            </div>
          )}
        </div>
      </div>
    );

    return (
      <div className="space-y-10">
        {/* Lead Information Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">NEW LEAD</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-6 gap-x-8">
            <div className="md:col-span-2">
              <RequiredLabel>Job Name</RequiredLabel>
              <input
                type="text"
                className={`${inputClasses} ${!formData.leadContactName ? 'border-red-300' : ''}`}
                placeholder="Enter job name"
                value={formData.leadContactName}
                onChange={(e) => setFormData({ ...formData, leadContactName: e.target.value })}
                required
              />
            </div>

            <div className="md:col-span-2">
              <RequiredLabel>Job Address</RequiredLabel>
              <textarea
                className={`${textAreaClasses} ${!formData.leadAddress ? 'border-red-300' : ''}`}
                placeholder="Enter job address"
                value={formData.leadAddress}
                onChange={(e) => setFormData({ ...formData, leadAddress: e.target.value })}
                required
              />
            </div>

            <div className="md:col-span-2">
              <RequiredLabel>City</RequiredLabel>
              <SearchableDropdown
                key="lead-city-nontabbed"
                options={cityOptions}
                value={cityOptions.find(opt => opt.name === formData.leadCity) || ''}
                onChange={handleCityChange}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option?.label || '')}
                placeholder="Search city..."
                className={`${inputClasses} ${!formData.leadCity ? 'border-red-300' : ''}`}
              />
            </div>
          </div>
        </div>

        {/* Client Information Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">CLIENT INFORMATION</h2>

          {/* Add Client Selector */}
          <div className="mb-4">
            <label className={labelClasses}>Choose from existing client</label>
            <ClientSearchSelect
              clients={clients}
              value={selectedClientId}
              onChange={handleClientSelect}
              className={inputClasses}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="md:col-span-2">
              <RequiredLabel>Client Name</RequiredLabel>
              <input
                type="text"
                className={`${inputClasses} ${!formData.clientName ? 'border-red-300' : ''}`}
                placeholder="Enter client name"
                value={formData.clientName}
                onChange={(e) => handleClientFieldChange('clientName', e.target.value)}
                required
              />
            </div>
            <div className="md:col-span-2">
              <label className={labelClasses}>Client Address</label>
              <textarea
                className={textAreaClasses}
                placeholder="Enter client address"
                value={formData.clientAddress}
                onChange={(e) => handleClientFieldChange('clientAddress', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClasses}>Client Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.clientCellPhone}
                onChange={(e) => handleClientFieldChange('clientCellPhone', e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <div className="flex items-center justify-between">
                <RequiredLabel>Client Email</RequiredLabel>
                <span className="text-xs text-gray-500">Type email and press Enter or comma to add</span>
              </div>
              <EmailTagInput
                emails={formData.clientEmails}
                onChange={handleEmailTagsChange}
                placeholder="Enter email address(es)"
                required
                className={!formData.clientEmails.length ? 'border-red-300' : ''}
              />
            </div>
          </div>
        </div>

        {/* Project/Contact Info Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">PROJECT/CONTACT INFO</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">



            <div className="mb-4 md:col-span-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="same-site-as-client-full"
                  checked={sameSiteAsClient}
                  onChange={() => setSameSiteAsClient(!sameSiteAsClient)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="same-site-as-client-full" className="ml-2 text-sm text-gray-700">
                  Same as client information
                </label>
              </div>
            </div>

            <div className="md:col-span-2">
              <label className={labelClasses}>Contact Name</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter contact name"
                value={formData.siteContactName}
                onChange={(e) => setFormData({ ...formData, siteContactName: e.target.value })}
                disabled={sameSiteAsClient}
              />
            </div>

            <div>
              <label className={labelClasses}>Contact Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.siteContactCellPhone}
                onChange={(e) => setFormData({ ...formData, siteContactCellPhone: e.target.value })}
                disabled={sameSiteAsClient}
              />
            </div>

            <div>
              <label className={labelClasses}>Contact Email</label>
              <input
                type="email"
                className={inputClasses}
                placeholder="Enter email address"
                value={formData.siteContactEmail}
                onChange={(e) => setFormData({ ...formData, siteContactEmail: e.target.value })}
                disabled={sameSiteAsClient}
              />
            </div>
          </div>
        </div>

        {/* Billing Information Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">BILLING INFORMATION</h2>

          <div className="mb-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="same-billing-as-client-full"
                checked={sameBillingAsClient}
                onChange={() => setSameBillingAsClient(!sameBillingAsClient)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label htmlFor="same-billing-as-client-full" className="ml-2 text-sm text-gray-700">
                Same as client information
              </label>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <div className="md:col-span-2">
              <label className={labelClasses}>Billing Name</label>
              <input
                type="text"
                className={inputClasses}
                placeholder="Enter billing name"
                value={formData.billingContactName}
                onChange={(e) => setFormData({ ...formData, billingContactName: e.target.value })}
                disabled={sameBillingAsClient}
              />
            </div>

            <div className="md:col-span-2">
              <label className={labelClasses}>Billing Address</label>
              <textarea
                className={textAreaClasses}
                placeholder="Enter billing address"
                value={formData.billingAddress}
                onChange={(e) => setFormData({ ...formData, billingAddress: e.target.value })}
                disabled={sameBillingAsClient}
              />
            </div>

            <div>
              <label className={labelClasses}>Billing Phone</label>
              <input
                type="tel"
                className={inputClasses}
                placeholder="(000) 000-0000"
                value={formData.billingPhone}
                onChange={(e) => setFormData({ ...formData, billingPhone: e.target.value })}
                disabled={sameBillingAsClient}
              />
            </div>

            <div>
              <label className={labelClasses}>Billing Email Address</label>
              <input
                type="email"
                className={inputClasses}
                placeholder="Enter billing email address"
                value={formData.billingEmail}
                onChange={(e) => setFormData({ ...formData, billingEmail: e.target.value })}
                disabled={sameBillingAsClient}
              />
            </div>
          </div>
        </div>

        {/* Project Manager Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">PROJECT MANAGER(S)</h2>

          <div className="mb-6">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2">
                <RequiredLabel
                  showInfo={true}
                  infoText="At least one project manager must be selected"
                >
                  Project Manager(s)
                </RequiredLabel>
              </div>
              <MultiSelectDropdown
                selectedManagers={selectedManagers}
                onManagerSelect={handleManagerSelect}
                onManagerRemove={handleManagerRemove}
              />
              {selectedManagers.length === 0 &&
                <p className="text-red-500 text-xs mt-1">Project manager is required</p>
              }
            </div>
          </div>
        </div>

        {/* Notes Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">NOTES</h2>

          <div className="mb-6">
            <textarea
              className={`${textAreaClasses} h-32`}
              placeholder="Enter any additional notes..."
              value={formData.leadNotes}
              onChange={(e) => setFormData({ ...formData, leadNotes: e.target.value })}
            />
          </div>
        </div>

        {/* Attachments Section */}
        <div>
          <h2 className="text-lg font-medium mb-4 text-gray-800 border-b pb-2">Attachments</h2>

          {/* Show selected files if any */}
          {formData.attachments.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Selected Files:</h3>
              <div className="grid grid-cols-2 gap-4">
                {formData.attachments.map((file, index) => renderFileItem(file, index))}
              </div>
            </div>
          )}

          {/* Upload area with drag and drop */}
          <div
            ref={dropAreaRef}
            className={`border-2 border-dashed ${isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'} rounded-lg p-8`}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center">
              <svg className={`w-16 h-16 ${isDragging ? 'text-blue-500' : 'text-gray-400'} mb-4`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <span className="text-sm text-gray-500">{isDragging ? 'Drop files here' : 'Select a file or drag and drop here'}</span>
              <span className="text-xs text-gray-400 mt-1">(JPG, PNG or PDF file size no more than 5GB)</span>
              <label htmlFor="file-upload" className="mt-4 px-4 py-2 text-sm text-blue-600 border border-blue-600 rounded hover:bg-blue-50 cursor-pointer">
                SELECT FILE
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleCreateLead}
            className="px-6 py-3 text-white bg-green-600 hover:bg-green-700 rounded-lg focus:outline-none"
          >
            Create Lead
          </button>
        </div>
      </div>
    );
  };

  // Add drag and drop event handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.target === dropAreaRef.current) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const droppedFiles = Array.from(e.dataTransfer.files);

    // Validate file size (max 5GB)
    const oversizedFiles = droppedFiles.filter(file => file.size > 5 * 1024 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      showToast('Some files exceed the 5GB size limit and will not be uploaded', 'error');
    }

    // Filter out files that are too large
    const validFiles = droppedFiles.filter(file => file.size <= 5 * 1024 * 1024 * 1024);

    // Generate preview URLs for the new files
    const newPreviewUrls = {};
    validFiles.forEach(file => {
      if (file.type.startsWith('image/')) {
        newPreviewUrls[file.name] = URL.createObjectURL(file);
      }
    });

    // Add new files to existing files
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, ...validFiles]
    }));

    // Update preview URLs
    setFilePreviewUrls(prev => ({
      ...prev,
      ...newPreviewUrls
    }));
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Full screen dialog */}
      <div className={`fixed inset-0 z-50 overflow-hidden bg-white ${isMaximized ? '' : 'mx-auto my-16 max-w-7xl rounded-lg shadow-xl'}`}>
        {/* Dialog header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">New Lead</h2>
          <div className="flex items-center space-x-2">
            {/* Undo button */}
            <button
              className={`text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 ${currentHistoryIndex <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
              onClick={handleUndo}
              disabled={currentHistoryIndex <= 0}
              title="Undo"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10h10a4 4 0 010 8H9m-6-8l4-4-4-4" />
              </svg>
            </button>

            {/* View toggle icon */}
            <button
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              onClick={toggleView}
              title={isTabbedView ? "Switch to full view" : "Switch to tabbed view"}
            >
              {isTabbedView ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                </svg>
              )}
            </button>

            {/* Maximize/restore icon */}
            <button
              className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100"
              onClick={toggleMaximize}
              title={isMaximized ? "Restore dialog size" : "Maximize dialog"}
            >
              {isMaximized ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 3v3a2 2 0 01-2 2H3m18 0h-3a2 2 0 01-2-2V3m0 18v-3a2 2 0 012-2h3M3 16h3a2 2 0 012 2v3" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>

            {/* Close icon */}
            <button
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Dialog content - scrollable area */}
        <div className="h-[calc(100vh-64px)] overflow-y-auto">
          <div className="p-6">
            {isTabbedView ? (
              <>
                {renderTabNavigation()}
                <div>
                  {renderTabContent()}
                </div>
              </>
            ) : (
              renderNonTabbedContent()
            )}
          </div>
        </div>
      </div>

      <ImagePreviewDialog
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
        imageUrl={previewImage}
      />
    </>
  );
};

export default NewLead; 